import React, { useState, useMemo } from "react";
import { Menu, MenuItem, Box, Typography, useTheme } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import CancelIcon from "@mui/icons-material/Cancel";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

/** Normaliza qualquer entrada para um status numérico válido.
 *  - null, undefined, '', 0, '0' => 0 (Pendente)
 *  - '1'...'5' => convertido para Number
 *  - valores fora do range => 0 (fallback seguro)
 */
function normalizeStatusInput(v) {
  if (v === null || v === undefined) return 0;
  if (v === "" || v === "0" || v === 0) return 0;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 && n <= 5 ? n : 0;
}

const STATUS_OPTIONS = [
  {
    value: 0,
    label: "Pendente",
    colorKey: "pending",
    icon: <HourglassEmptyIcon />,
  },
  {
    value: 1,
    label: "Em Atendimento",
    colorKey: "inProgress",
    icon: <AutorenewIcon />,
  },
  {
    value: 2,
    label: "Concluída",
    colorKey: "success",
    icon: <CheckCircleOutlineIcon />,
  },
  {
    value: 3,
    label: "Contato Futuro",
    colorKey: "future",
    icon: <ForwardToInboxIcon />,
  },
  {
    value: 4,
    label: "Perdida",
    colorKey: "unsuccessful",
    icon: <HighlightOffIcon />,
  },
  {
    value: 5,
    label: "Cancelada",
    colorKey: "canceled",
    icon: <CancelIcon />,
  },
];

const StatusSelector = ({ currentStatus, onStatusChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();

  // Garante que o status atual seja coerente
  const currentStatusNormalized = useMemo(
    () => normalizeStatusInput(currentStatus),
    [currentStatus]
  );

  const currentStatusDetails =
    STATUS_OPTIONS.find((s) => s.value === currentStatusNormalized) ||
    STATUS_OPTIONS[0]; // fallback: Pendente

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = (statusValue) => {
    const normalized = normalizeStatusInput(statusValue);
    onStatusChange?.(normalized);
    handleClose();
  };

  return (
    <>
      {/* Botão de status estilizado */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "12px 16px",
          borderRadius: "12px",
          backgroundColor: theme.palette.status[currentStatusDetails.colorKey],
          cursor: "pointer",
          color: theme.palette.common.white,
          boxShadow: theme.shadows[1],
          "&:hover": {
            boxShadow: theme.shadows[4],
          },
        }}
        onClick={handleClick}
        id="status-selector"
        aria-haspopup="true"
        aria-controls={Boolean(anchorEl) ? "status-selector-menu" : undefined}
        aria-expanded={Boolean(anchorEl) ? "true" : undefined}
      >
        {/* Ícone e texto do status */}
        {currentStatusDetails.icon}
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: "bold",
              p: 0,
              m: 0,
              lineHeight: 1,
            }}
          >
            {currentStatusDetails.label}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              p: 0,
              m: 0,
              lineHeight: 1,
              display: "block",
              mt: "2px",
            }}
          >
            Status
          </Typography>
        </Box>
      </Box>

      {/* Menu suspenso para seleção de status */}
      <Menu
        id="status-selector-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "status-selector",
        }}
      >
        {STATUS_OPTIONS.map((option) => (
          <MenuItem
            key={`status-${option.value}`}
            onClick={() => handleStatusChange(option.value)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              color: theme.palette.text.primary,
              "&:hover": {
                backgroundColor: theme.palette.status[option.colorKey],
                color: theme.palette.common.white,
              },
            }}
            selected={currentStatusNormalized === option.value}
          >
            {option.icon}
            <Typography variant="subtitle1">{option.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default StatusSelector;
